import { FC, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import IconActionItem from './Items/IconItem';
import ConfirmActionModal from './Modals/ConfirmAction';
import CancelBookingModal from './Modals/CancelBooking';
import RefundModal from '../Payments/components/modals/RefundModal';
import { useActions } from 'src/hooks/useActions';
import { useTypedSelector } from 'src/hooks/useTypeSelector';
import { IOrderFetch, IPayOrderFetch } from 'src/models/IOrder';
import './style.scss';

interface IProps {
  order: IOrderFetch;
}

type TModalState = {
  refundHost: boolean;
  refundTotal: boolean;
  changeVan: boolean;
  cancel: boolean;
};

const Actions: FC<IProps> = ({ order }) => {
  const { actionOrder, sentEmail } = useActions();
  const { enqueueSnackbar } = useSnackbar();
  const { role } = useTypedSelector(state => state.auth);
  const { isLoading, result, action } = useTypedSelector(state => state.order);

  const [refund, setRefund] = useState({ price: 0 } as IPayOrderFetch);
  const [openModal, setOpenModal] = useState<TModalState>({
    refundHost: false,
    refundTotal: false,
    changeVan: false,
    cancel: false
  });

  useEffect(() => {
    if (order.handle_payments.length && role === 'admin') {
      const payments = order.handle_payments.filter(
        payment =>
          payment.type !== 'refund_payment' &&
          payment.note !== 'sprinter-rentals fee' &&
          !payment.note.startsWith('$250.00 On Hold')
      );
      const refund = payments.reduce((max, el) => {
        return el.price > max.price ? el : max;
      }, order.handle_payments[0]);
      setRefund(refund);
    }
  }, [order.handle_payments]);

  useEffect(() => {
    if (!isLoading.action && result.action) {
      if (action.data === 'email') {
        result.action === 200
          ? enqueueSnackbar('Email sent successfully', { variant: 'success' })
          : enqueueSnackbar('Email was not sent', { variant: 'error' });
      } else if (action.data === 'hold amount') {
        result.action === 200
          ? enqueueSnackbar('Hold Amount was successful', { variant: 'success' })
          : enqueueSnackbar('Hold Amount failed', { variant: 'error' });
      } else if (action.data === 'refund') {
        result.action === 200
          ? enqueueSnackbar('Refund was successful', { variant: 'success' })
          : enqueueSnackbar('Refund failed', { variant: 'error' });
      }
    }
  }, [isLoading.action, result.action]);

  const handleOpenModal = (name: string) => {
    setOpenModal(prev => ({ ...prev, [name]: true }));
  };

  const handleCloseModal = (name: string) => {
    setOpenModal(prev => ({ ...prev, [name]: false }));
  };

  const actions = [
    {
      title: 'Cancel booking',
      subtitle: 'guest request or no van',
      onOpenModal: () => handleOpenModal('cancel'),
      isDisabled: order.status === 'deleted' || order.status === 'blocked'
    },
    {
      title: 'Resign e-agreement',
      subtitle: 'deletes Old + emails',
      name: 'resign',
      isDisabled: order.status === 'deleted' || order.status === 'blocked'
    },
    {
      title: 'Van change',
      subtitle: '1st drag order to new van in calendar',
      onOpenModal: () => handleOpenModal('changeVan'),
      isDisabled: order.status === 'deleted' || order.status === 'blocked'
    },
    {
      title: 'Refund From Down-Pay Only',
      subtitle: 'AO',
      name: 'refund only',
      isAdmin: true,
      isDisabled: order.status === 'blocked'
    },
    {
      title: 'Refund From Host-Pay Only',
      subtitle: 'AO',
      onOpenModal: () => handleOpenModal('refundHost'),
      isAdmin: true,
      isDisabled: order.status === 'blocked'
    },
    {
      title: 'Refund on Total',
      subtitle: 'for correct host/platform split',
      onOpenModal: () => handleOpenModal('refundTotal'),
      isAdmin: true,
      isDisabled: order.status === 'blocked' || !refund.price
    }
  ];

  const modals = [
    {
      action: 'Refund',
      title: 'Refund All-Due Payments',
      text: 'This action will refund the full amount to the original payment method. This cannot be undone',
      open: openModal.refundHost,
      onClose: () => handleCloseModal('refundHost'),
      onSubmit: () => actionOrder(order.id, 'cancel 4 guest')
    },
    {
      action: 'Send',
      title: 'Change Van',
      text: 'Please swap order on calendar before send email',
      open: openModal.changeVan,
      onClose: () => handleCloseModal('changeVan'),
      onSubmit: () => sentEmail(order.id, 'Van changed. 1st move')
    }
  ];

  return (
    <>
      <div className='order-show__title' style={{ color: 'rgba(255, 142, 0, 1)' }}>
        Click Action
        <span className='order-cart__subtitle'>system executes steps for you</span>
      </div>
      {actions.map((action, i) => (
        <IconActionItem
          key={i}
          title={action.title}
          subtitle={action.subtitle}
          name={action.name}
          id={order.id}
          isDisabled={action.isDisabled}
          isAdmin={action.isAdmin}
          onOpenModal={action.onOpenModal}
        />
      ))}
      {modals.map((modal, i) => (
        <ConfirmActionModal
          key={i}
          action={modal.action}
          title={modal.title}
          text={modal.text}
          open={modal.open}
          onClose={modal.onClose}
          onSubmit={modal.onSubmit}
        />
      ))}
      <CancelBookingModal
        id={order.id}
        open={openModal.cancel}
        onClose={() => handleCloseModal('cancel')}
      />
      <RefundModal
        refund={refund}
        handleClose={() => handleCloseModal('refundTotal')}
        open={openModal.refundTotal}
      />
    </>
  );
};

export default Actions;
